/**
 * Alert families (1B S7-11, moved here in W2-6-1): one map for the API (AlertCase.family, the staff inbox), the summaries
 * (the network text counts families, not every rule) and the web. Same id map and keyword order as 1B.
 */

/** the family order (the tie-break after the count) */
export const ALERT_FAMILIES = ['cancel', 'legal', 'publicity', 'conduct', 'angry', 'red_flag', 'finance', 'approval', 'gold', 'sales', 'service', 'watch', 'other'] as const;

/** what an alert is about */
export type AlertFamily = (typeof ALERT_FAMILIES)[number];

export const FAMILY_LABELS: Record<AlertFamily, { he: string; en: string }> = {
  cancel: { he: 'בקשות ביטול / החזר', en: 'cancel / refund requests' },
  legal: { he: 'איומים משפטיים או במשטרה', en: 'legal or police threats' },
  publicity: { he: 'איומי פרסום, תקשורת או רגולטור', en: 'publicity, media or regulator threats' },
  conduct: { he: 'התנהלות נציג', en: 'agent conduct' },
  angry: { he: 'לקוחות כועסים', en: 'angry customers' },
  red_flag: { he: 'דגלים אדומים', en: 'red flags' },
  finance: { he: 'מימון', en: 'finance' },
  approval: { he: 'ממתינים לאישור', en: 'waiting for approval' },
  gold: { he: 'לקוחות זהב', en: 'gold customers' },
  sales: { he: 'הזדמנויות מכירה', en: 'sales opportunities' },
  service: { he: 'שירות שלא הושלם', en: 'unfinished service' },
  watch: { he: 'מעקב צוות', en: 'team watch' },
  other: { he: 'אחרות', en: 'other' },
};

/** one key per rule name — NFKC, no quotes / punctuation / dashes, single spaces, lower case */
export function normalizeRuleName(name: string): string {
  return name
    .normalize('NFKC')
    .replace(/[״"'׳.,:;!?()/–—-]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

const ids = (list: string[], family: AlertFamily): [string, AlertFamily][] => list.map((id) => [id, family]);
const FAMILY_BY_ID = new Map<string, AlertFamily>([
  ...ids(['car-01', 'car-12', 'car-17', 'gen-01', 'gen-02'], 'legal'),
  ...ids(['car-03', 'car-19', 'gen-03', 'gen-09'], 'publicity'),
  ...ids(['car-02', 'car-22', 'car-20'], 'cancel'),
  ...ids(['car-04', 'car-16', 'car-21', 'gen-04'], 'conduct'),
  ...ids(['car-05', 'gen-06'], 'angry'),
  ...ids(['car-13', 'gen-05'], 'red_flag'),
  // D-179: the finance / tax offence is the critical-flag family's own alert; the routine (record-only) red flags stay in it too
  ...ids(['car-23', 'car-24', 'gen-10', 'lab-23', 'ret-23', 'ins-32'], 'red_flag'),
  ...ids(['car-08'], 'finance'),
  ...ids(['car-06'], 'approval'),
  ...ids(['car-15', 'car-18'], 'gold'),
  ...ids(['car-07', 'car-09', 'car-10', 'car-11', 'car-14'], 'sales'),
  // D-151 (W4A-4): the lab rules (labs.ruleset.ts), Orel's approved defaults — the id wins over the name keywords
  ...ids(['lab-01', 'lab-02'], 'legal'),
  ...ids(['lab-03', 'lab-05'], 'publicity'),
  ...ids(['lab-04'], 'conduct'),
  ...ids(['lab-18'], 'cancel'),
  ...ids(['lab-07', 'lab-10', 'lab-11'], 'red_flag'),
  ...ids(['lab-13', 'lab-14', 'lab-22'], 'finance'),
  ...ids(['lab-12', 'lab-19', 'lab-20'], 'service'),
  // D-160 retail: the rest is 'other', never a name keyword
  ...ids(['ret-01', 'ret-02'], 'legal'),
  ...ids(['ret-03', 'ret-05'], 'publicity'),
  ...ids(['ret-10'], 'cancel'),
  ...ids(['ret-04', 'ret-06', 'ret-14'], 'conduct'),
  ...ids(['ret-07'], 'red_flag'),
  ...ids(['ret-11', 'ret-12', 'ret-13', 'ret-15', 'ret-16', 'ret-17', 'ret-18', 'ret-19', 'ret-20', 'ret-21', 'ret-22'], 'other'),
  // D-159 insurance: compliance / seller acts are agent conduct
  ...ids(['ins-01', 'ins-02', 'ins-08'], 'legal'),
  ...ids(['ins-03', 'ins-09'], 'publicity'),
  ...ids(['ins-04', 'ins-11', 'ins-12', 'ins-13', 'ins-14', 'ins-15', 'ins-16', 'ins-17'], 'conduct'),
  ...ids(['ins-07'], 'red_flag'),
  // D-170 (Maora v2)
  ...ids(['ins-19', 'ins-20', 'ins-24', 'ins-26', 'ins-27', 'ins-28'], 'cancel'),
  ...ids(['ins-18', 'ins-25'], 'angry'),
  ...ids(['ins-21', 'ins-22'], 'service'),
  ...ids(['ins-23', 'ins-29', 'ins-30'], 'sales'),
  ...ids(['ins-31'], 'conduct'),
]);

/** in this order: the first family with a keyword in the normalised name wins */
const FAMILY_KEYWORDS: [AlertFamily, string[]][] = [
  ['conduct', ['איים על הלקוח', 'זלזול', 'פוגעני']],
  ['cancel', ['ביטול', 'לבטל', 'כסף בחזרה', 'החזר כספי']],
  ['legal', ['תביעה', 'עורך דין', 'משטרה', 'ספאם', 'להפסיק לפנות']],
  ['publicity', ['פרסום', 'רשתות', 'ביקורות', 'תקשורת', 'רגולטור']],
  ['angry', ['כועס']],
  ['red_flag', ['דגל אדום']],
  ['finance', ['מימון']],
  ['approval', ['לאישור']],
  ['gold', ['זהב']],
];

/** the family of an alert — watch rules, then the in-house rule ids, then keywords of the (normalised) rule name */
export function alertFamily(ruleUuid: string, ruleName: string): AlertFamily {
  if (ruleUuid.startsWith('watch-')) return 'watch';
  const byId = FAMILY_BY_ID.get(ruleUuid);
  if (byId) return byId;
  const n = normalizeRuleName(ruleName);
  for (const [family, words] of FAMILY_KEYWORDS) if (words.some((w) => n.includes(w))) return family;
  return 'other';
}

/** car-NN / gen-NN / lab-NN (D-151) / ret-NN (D-160) / ins-NN (D-159) = ours; watch-* = watch; anything else is a NIVISION rule uuid */
export function ruleOrigin(ruleUuid: string): 'ours' | 'watch' | 'nivision' {
  if (/^(car|gen|lab|ret|ins)-\d{2}$/.test(ruleUuid)) return 'ours';
  if (/^watch-/.test(ruleUuid)) return 'watch';
  return 'nivision';
}
